import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, UserRoundPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CpfCheckResult, physicalClientsApi } from "@/lib/api";
import { FieldError, getCustomerRegistrationErrorMessage, getFieldErrors } from "@/lib/customerRegistrationError";
import {
  CPF_ALREADY_REGISTERED_CODE,
  CPF_ALREADY_REGISTERED_MESSAGE,
  getPhysicalClientConflict,
} from "@/lib/physicalClientError";
import { isValidCpf, normalizeCpf } from "@/lib/validators/cpf";
import { PhysicalClientForm, PhysicalClientFormValues } from "./component/PhysicalClientForm";
import { EMPTY_CREATE_FORM, toCreatePayload } from "./component/physicalClientFormSchema";
import { physicalClientsQueryKey } from "./component/physicalClientQueryKeys";

/**
 * Backoffice > Clientes > Pessoa física > Novo. Endereços são cadastrados no detalhe, depois de salvar.
 * A checagem de CPF e o cadastro chamam a API direto (sem useMutation), para o CPF não ficar no cache do TanStack.
 */
export default function PhysicalClientCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [duplicate, setDuplicate] = useState<Pick<CpfCheckResult, "physicalClientId"> | null>(null);
  const [isCheckingCpf, setIsCheckingCpf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldError[]>([]);

  const checkCpf = async (value: string) => {
    if (!isValidCpf(value)) {
      setDuplicate(null);
      return;
    }
    setIsCheckingCpf(true);
    try {
      const result = await physicalClientsApi.checkCpf(normalizeCpf(value));
      setDuplicate(result.exists ? { physicalClientId: result.physicalClientId } : null);
    } catch {
      // Sem a pré-checagem, o salvar ainda é recusado pela API com 409.
      setDuplicate(null);
    } finally {
      setIsCheckingCpf(false);
    }
  };

  const submit = async (values: PhysicalClientFormValues) => {
    setServerErrors([]);
    setIsSubmitting(true);
    try {
      const created = await physicalClientsApi.create(toCreatePayload(values));
      toast.success("Cliente cadastrado.");
      void queryClient.invalidateQueries({ queryKey: physicalClientsQueryKey });
      navigate(`/backoffice/clientes/pf/${created.id}`, { state: { tab: "addresses" } });
    } catch (error) {
      const conflict = getPhysicalClientConflict(error);
      if (conflict?.code === CPF_ALREADY_REGISTERED_CODE) {
        setDuplicate({ physicalClientId: conflict.physicalClientId });
        toast.error(CPF_ALREADY_REGISTERED_MESSAGE);
      } else {
        setServerErrors(getFieldErrors(error));
        toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível cadastrar o cliente."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/backoffice/clientes/pf")}>
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          <UserRoundPlus className="h-6 w-6 text-primary" aria-hidden="true" />
          <h1 className="text-2xl font-bold tracking-tight">Novo cliente pessoa física</h1>
        </div>
      </div>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Dados do cliente</CardTitle>
          <CardDescription>
            O CPF não pode ser alterado depois do cadastro. Os endereços são cadastrados na próxima tela.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PhysicalClientForm
            mode="create"
            defaultValues={EMPTY_CREATE_FORM}
            isSubmitting={isSubmitting}
            serverErrors={serverErrors}
            duplicate={duplicate}
            isCheckingCpf={isCheckingCpf}
            onCpfBlur={(value) => void checkCpf(value)}
            onCpfChange={() => setDuplicate(null)}
            onSubmit={submit}
            onCancel={() => navigate("/backoffice/clientes/pf")}
            submitLabel="Cadastrar cliente"
          />
        </CardContent>
      </Card>
    </div>
  );
}
